// src/pages/Register.jsx
import React, { useState, useEffect } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, AlertCircle, Clock, Lock } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import { clearUserStorage } from "../utils/userStorage";
import {
  ONTARIO_ONLY_ITEM_IDS,
  excludesOntarioOnly,
  getRegistrationProvince,
  saveRegistrationProvince,
} from "../utils/registrationProvince";
import talentLogo from "../assets/talentlogo2.png";

const defaultItems = [
  { id: "profile", label: "Profile", status: "incomplete" },
  { id: "documents", label: "Documents", status: "incomplete" },
  { id: "td1-tax", label: "2026 TD1 TAX FORM", status: "incomplete" },
  { id: "tdon-tax", label: "2026 PROVINCIAL TD1 TAX FORM", status: "incomplete" },
  { id: "accessibility", label: "Accessibility Standards for Customer Service", status: "incomplete" },
  { id: "overtime-avg", label: "Agreement for Overtime Averaging 2026", status: "incomplete" },
  { id: "esa-hours", label: "Agreement to Exceed Ontario ESA Hours of Work", status: "incomplete" },
  { id: "employment-standards", label: "Employment Standards Rights for Temporary Help Agency Assignment Employees", status: "incomplete" },
  { id: "overtime-pay", label: "Hours of Work and Overtime Pay", status: "incomplete" },
  { id: "human-rights", label: "Human Rights Code", status: "incomplete" },
  { id: "media-release", label: "Media Release and Confidentiality Agreement", status: "incomplete" },
  { id: "labour-info", label: "Ministry of Labour Information Acceptance Sheet", status: "incomplete" },
  { id: "holiday-ack", label: "Statutory Holiday Acknowledgement", status: "incomplete" },
  { id: "payroll-deduction", label: "Voluntary Payroll Deduction Authorization", status: "incomplete" },
  { id: "whmis-safety", label: "WHMIS, and Health & Safety Acknowledgement", status: "incomplete" },
  { id: "violence-policy", label: "Workplace Violence Policy", status: "incomplete" },
  { id: "additional-info", label: "Additional Info", status: "incomplete" },
  { id: "health-safety", label: "Health & Safety", status: "incomplete" },
  { id: "whmis", label: "WHMIS", status: "incomplete" },
  { id: "work-preferences", label: "Work Preferences", status: "incomplete" },
  { id: "certifications", label: "Certifications", status: "incomplete" },
  { id: "availability", label: "Availability", status: "incomplete" },
];

const StatusIcon = ({ status }) => {
  switch (status) {
    case "complete": return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case "in-progress": return <Clock className="h-5 w-5 text-yellow-500" />;
    default: return <AlertCircle className="h-5 w-5 text-red-500" />;
  }
};

const StatusBadge = ({ status }) => {
  const text = status === "complete" ? "Complete" : status === "in-progress" ? "In Progress" : "Incomplete";
  const cls = status === "complete" ? "bg-green-100 text-green-800"
    : status === "in-progress" ? "bg-yellow-100 text-yellow-800"
      : "bg-red-100 text-red-800";
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${cls}`}>{text}</span>;
};

// Every other section stays locked (blurred) until this one is complete
const GATE_ITEM_ID = "profile";

const ProfileRequiredModal = ({ open, onClose, onGoToProfile }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-required-title"
        className="w-full max-w-md rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center px-6 pt-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <Lock className="h-6 w-6 text-red-600" />
          </div>
          <h3 id="profile-required-title" className="mt-4 text-lg font-semibold text-gray-900">
            Complete your Profile first
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Please complete the Profile section before accessing any other registration forms.
          </p>
        </div>
        <div className="mt-6 flex gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={onGoToProfile}
            autoFocus
            className="flex-1 rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-900"
          >
            Go to Profile
          </button>
        </div>
      </div>
    </div>
  );
};

const RegistrationChecklist = ({ initialItems = defaultItems, onSubmit, onSignOut }) => {
  usePageMeta("Register");
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState(initialItems);
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Opened when a user clicks a locked section, or is bounced back here from one (see ItemDetail)
  const [showProfileModal, setShowProfileModal] = useState(!!location.state?.profileRequired);
  // Province from the Profile address: Ontario-only items (TDON) are hidden for other provinces
  const [province, setProvince] = useState(() => getRegistrationProvince(localStorage.getItem("accountId")));

  const isProfileComplete =
    items.find((i) => i.id === GATE_ITEM_ID)?.status === "complete";
  const isLockedByProfile = (item) => item.id !== GATE_ITEM_ID && !isProfileComplete;
  const hidesOntarioOnly = ONTARIO_ONLY_ITEM_IDS.length > 0 && excludesOntarioOnly(province);
  const visibleItems = hidesOntarioOnly
    ? items.filter((i) => !ONTARIO_ONLY_ITEM_IDS.includes(i.id))
    : items;

  // Clear the redirect flag so a refresh doesn't re-open the modal
  useEffect(() => {
    if (location.state?.profileRequired) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Check if already registered when component mounts
  // IMPORTANT: Skip this check for tempstaff (fieldstaff/outsourcestaff) - they should never see registration popups
  useEffect(() => {
    const checkRegistration = async () => {
      const accountId = localStorage.getItem("accountId");
      if (!accountId) return;

      // Check user type first - tempstaff should never see registration popups
      // Check both localStorage and JWT token for userType (JWT is more reliable)
      let userType = localStorage.getItem("userType")?.toLowerCase();

      // Fallback: decode from JWT token if not in localStorage
      if (!userType) {
        try {
          const token = localStorage.getItem("token");
          if (token && token !== 'null' && token !== 'undefined') {
            const payload = token.split('.')[1];
            if (payload) {
              const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
              userType = decoded?.type?.toLowerCase();
            }
          }
        } catch (e) {
          console.error("Error decoding token for userType:", e);
        }
      }

      if (userType === 'fieldstaff' || userType === 'outsourcestaff' || userType === 'outsourcedstaff') {
        // Tempstaff users should be redirected immediately to availability, no popup
        console.log("Tempstaff user detected on register page, redirecting to availability immediately");
        navigate('/availability', { replace: true });
        return;
      }

      // Hard-redirect after clearing storage so ProtectedRoute cannot bounce
      // the user back to /register (soft navigate keeps React state alive).
      const hardRedirect = (redirectPath, clearAuth = false) => {
        if (clearAuth) {
          clearUserStorage();
          [
            "token", "accountId", "userType", "isAdmin",
            "impersonatedAccountId", "impersonatedUserType", "impersonating",
          ].forEach((k) => {
            localStorage.removeItem(k);
            sessionStorage.removeItem(k);
          });
        }
        window.location.replace(redirectPath);
      };

      const resolveCompletedRedirect = (type) => {
        const t = String(type || userType || "").toLowerCase();
        if (t === "fieldstaff" || t === "outsourcestaff" || t === "outsourcedstaff") return "/availability";
        if (t === "client") return "/client-dashboard";
        if (t === "admin" || t === "officestaff" || t === "manager") return "/dashboard";
        return "/login";
      };

      try {
        const response = await axios.get(`${BASE_URL}/api/registration/${accountId}/state`);
        if (response.data?.alreadyRegistered) {
          showSuccess(response.data.message || "Your registration has been completed.", () => {
            const redirectPath = resolveCompletedRedirect(response.data.userType);
            const shouldClearAuth = redirectPath === "/login";
            hardRedirect(redirectPath, shouldClearAuth);
          });
        }
      } catch (err) {
        if (err.response?.data?.alreadyRegistered) {
          const redirectPath = resolveCompletedRedirect(err.response.data.userType);
          const shouldClearAuth = redirectPath === "/login";
          showSuccess(err.response.data.message || "Your registration has been completed.", () => {
            hardRedirect(redirectPath, shouldClearAuth);
          });
        }
      }
    };
    checkRegistration();
  }, [navigate]);
  // const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    // Keep registration drafts/status in localStorage so the user can resume later on this browser
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    navigate("/login");
  };

  // // Load statuses from backend API
  // useEffect(() => {
  //   const loadStatuses = async () => {
  //     const accountId = localStorage.getItem("accountId");
  //     if (!accountId) {
  //       setLoading(false);
  //       return;
  //     }

  //     try {
  //       const response = await axios.get(`${BASE_URL}/api/registration/${accountId}`);
  //       const backendItems = response.data.items;

  //       // Merge backend statuses with frontend items
  //       setItems(prev => 
  //         prev.map(frontendItem => {
  //           const backendItem = backendItems.find(bi => 
  //             bi.label === frontendItem.label || 
  //             bi.label.includes(frontendItem.label.split(' ')[0])
  //           );
  //           return backendItem ? { ...frontendItem, status: backendItem.status } : frontendItem;
  //         })
  //       );
  //     } catch (error) {
  //       console.error("Failed to load statuses:", error);
  //       // Fallback to localStorage
  //       setItems(prev =>
  //         prev.map((i) => {
  //           const saved = localStorage.getItem(`status_${i.id}`);
  //           return saved ? { ...i, status: saved } : i;
  //         })
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   loadStatuses();
  //   window.addEventListener("storage", loadStatuses);
  //   return () => window.removeEventListener("storage", loadStatuses);
  // }, []);

  useEffect(() => {
    // Merge backend statuses (source of truth) with any local overrides written
    // after the last sync (e.g. the user just finished a form in this session).
    const mergeStatuses = (backendMap) => {
      const accountId = localStorage.getItem("accountId");
      setItems((prev) =>
        prev.map((i) => {
          const scoped = accountId ? localStorage.getItem(`status_${accountId}_${i.id}`) : null;
          const legacy = localStorage.getItem(`status_${i.id}`);
          const local = scoped || legacy;
          const backend = backendMap[i.id];

          // Pick whichever is "further ahead":
          // complete > in-progress > incomplete
          const rank = { complete: 2, "in-progress": 1, incomplete: 0 };
          const best =
            (rank[local] ?? -1) >= (rank[backend] ?? -1) ? local : backend;
          return best ? { ...i, status: best } : i;
        })
      );
    };

    const loadFromLocalOnly = () => {
      const accountId = localStorage.getItem("accountId");
      setItems((prev) =>
        prev.map((i) => {
          const scoped = accountId ? localStorage.getItem(`status_${accountId}_${i.id}`) : null;
          const legacy = localStorage.getItem(`status_${i.id}`);
          const saved = scoped || legacy;
          return saved ? { ...i, status: saved } : i;
        })
      );
    };

    const syncFromBackend = async () => {
      const accountId = localStorage.getItem("accountId");
      if (!accountId) {
        loadFromLocalOnly();
        return;
      }
      try {
        const { data } = await axios.get(
          `${BASE_URL}/api/registration/${accountId}`
        );
        // The backend stores content names that differ from frontend display labels.
        // This map translates backend content names → frontend item IDs.
        const backendNameToId = {
          "Profile": "profile",
          "Documents": "documents",
          "TD1 TAX FORM": "td1-tax",
          "TDON TAX FORM": "tdon-tax",
          "Accessibility Standards": "accessibility",
          "Overtime Averaging Agreement": "overtime-avg",
          "ESA Hours Agreement": "esa-hours",
          "Employment Standards": "employment-standards",
          "Overtime Pay": "overtime-pay",
          "Human Rights": "human-rights",
          "Media Release": "media-release",
          "Labour Information": "labour-info",
          "Holiday Acknowledgment": "holiday-ack",
          "Payroll Deduction": "payroll-deduction",
          "WHMIS Safety": "whmis-safety",
          "Violence Policy": "violence-policy",
          "Additional Info": "additional-info",
          "Health & Safety": "health-safety",
          "WHMIS": "whmis",
          "Work Preferences": "work-preferences",
          "Certifications": "certifications",
          "Availability": "availability",
        };

        const backendMap = {};
        (data.items || []).forEach((item) => {
          const frontendId = backendNameToId[item.label];
          if (frontendId) backendMap[frontendId] = item.status;
        });

        // Write backend statuses into localStorage so quiz components
        // (WHMIS, HealthSafety) can pick them up via their own reads.
        Object.entries(backendMap).forEach(([id, status]) => {
          const key = `status_${accountId}_${id}`;
          const existing = localStorage.getItem(key);
          const rank = { complete: 2, "in-progress": 1, incomplete: 0 };
          if ((rank[status] ?? -1) > (rank[existing] ?? -1)) {
            localStorage.setItem(key, status);
          }
        });

        mergeStatuses(backendMap);

        if (data.province !== undefined) {
          setProvince(data.province || "");
          saveRegistrationProvince(accountId, data.province);
        }
      } catch (err) {
        console.warn("[Register] Could not load statuses from backend, falling back to localStorage:", err.message);
        loadFromLocalOnly();
      }
    };

    setItems(initialItems);
    syncFromBackend();

    // Re-hydrate from localStorage when navigating back into this page
    // (backend sync already ran; focus/storage events keep things in sync
    //  within the same browser session)
    const onFocus = () => loadFromLocalOnly();
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", loadFromLocalOnly);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", loadFromLocalOnly);
    };
  }, [initialItems]);

  const handleItemClick = (item) => {
    if (isLockedByProfile(item)) {
      setShowProfileModal(true);
      return;
    }

    // Block Certifications until Work Preferences is completed
    if (item.id === "certifications") {
      const workPref = items.find((i) => i.id === "work-preferences");
      if (!workPref || workPref.status !== "complete") {
        showError(
          "Please complete the Work Preferences section before filling in Certifications."
        );
        return;
      }
    }

    navigate(`/register/${item.id}`, { state: { items } });
  };

  const handleSubmit = async () => {
    // Exclude optional items from the completion requirement (any certification-related items)
    const isOptional = (id) => typeof id === "string" && id.toLowerCase().includes("certification");
    const requiredItems = visibleItems.filter(i => !isOptional(i.id));
    const incompleteRequired = requiredItems.filter(i => i.status !== "complete");
    
    // ✅ Check if mandatory quizzes are completed
    const quizItems = items.filter(i => 
      i.label === "Health & Safety" || i.label === "WHMIS"
    );
    const incompleteQuizzes = quizItems.filter(i => i.status !== "complete");
    
    if (incompleteQuizzes.length > 0) {
      const quizNames = incompleteQuizzes.map(i => i.label).join(", ");
      setSubmitErr(`⚠️ You must complete all mandatory quizzes: ${quizNames}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (incompleteRequired.length > 0) {
      const names = incompleteRequired.map(i => i.label).join(", ");
      setSubmitErr(`⚠️ Please complete required items: ${names}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitErr("");
    setSubmitting(true);

    try {
      const accountId = localStorage.getItem("accountId");

      if (process.env.NODE_ENV !== 'production') {
        console.log("[REGISTER] ▶ Submit clicked");
        console.log("[REGISTER] accountId:", accountId);
        console.log("[REGISTER] localStorage keys:", Object.keys(localStorage));
      }

      if (!accountId) {
        setSubmitErr("Account ID not found. Please refresh the page and try again.");
        setSubmitting(false);
        return;
      }

      // Collect all form data from localStorage
      const allFormData = {};
      const allStatuses = {};

      // Get all localStorage keys that start with 'form_' or 'status_' for this user
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith(`form_${accountId}_`) || key.startsWith(`status_${accountId}_`))) {
          const value = localStorage.getItem(key);
          try {
            const parsed = JSON.parse(value);
            if (key.startsWith(`form_${accountId}_`)) {
              allFormData[key] = parsed;
            } else {
              allStatuses[key] = parsed;
            }
          } catch {
            // If not JSON, store as string
            if (key.startsWith(`form_${accountId}_`)) {
              allFormData[key] = value;
            } else {
              allStatuses[key] = value;
            }
          }
        }
      }

      // Build normalized payload; include a top-level profile object if present
      const profileKey = `form_${accountId}_profile`;
      const normalized = { ...allFormData };
      if (allFormData[profileKey]) {
        normalized.profile = allFormData[profileKey];
      }

      // Diagnostics for payload size
      const payload = { formData: normalized, statuses: allStatuses };
      const approxPayloadSizeKb = Math.round(JSON.stringify(payload).length / 1024);
      if (process.env.NODE_ENV !== 'production') {
        console.log("[REGISTER] Aggregated form keys:", Object.keys(allFormData));
        console.log("[REGISTER] Aggregated status keys:", Object.keys(allStatuses));
        console.log(`[REGISTER] Payload size ~${approxPayloadSizeKb} KB`);
      }

      // Submit all data to backend
      const url = `${BASE_URL}/api/registration/${accountId}/submit`;
      if (process.env.NODE_ENV !== 'production') console.log("[REGISTER] POST", url);
      const res = await axios.post(url, payload);
      if (process.env.NODE_ENV !== 'production') console.log("[REGISTER] Response:", res.status, res.data);

      if (res.data.alreadyRegistered) {
        // Get accountId before showing message
        const currentAccountId = localStorage.getItem("accountId");

        // Show message with OK button
        showSuccess(res.data.message, () => {
          // Clear form data and auth state
          clearUserStorage();

          // Clean up form data for this account
          if (currentAccountId) {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith(`form_${currentAccountId}_`) || key.startsWith(`status_${currentAccountId}_`)) {
                localStorage.removeItem(key);
              }
            });
          }

          // Always redirect to signup for already registered users
          navigate('/signup');
        });
        return;
      }

      if (res.data.success) {
        showSuccess("Registration submitted successfully!", () => {
          // Clear localStorage after successful submission
          Object.keys(allFormData).forEach(key => localStorage.removeItem(key));
          Object.keys(allStatuses).forEach(key => localStorage.removeItem(key));
          if (onSubmit) onSubmit(res.data);
          // Redirect to login
          navigate('/login');
        });
      } else {
        throw new Error(res.data.error || "Submission failed");
      }
    } catch (err) {
      console.error("[REGISTER] ❌ Submit error:", err);
      console.error("[REGISTER] Error response:", err.response?.data);
      console.error("[REGISTER] Error status:", err.response?.status);

      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message || "Unknown error";
      const currentAccountId = localStorage.getItem("accountId");
      
      // Handle quiz validation error specifically
      if (errorData?.error === "QUIZZES_INCOMPLETE") {
        const quizList = errorData?.incompleteQuizzes?.join(", ") || "Health & Safety, WHMIS";
        setSubmitErr(`⚠️ You must complete all mandatory quizzes: ${quizList}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSubmitting(false);
        return;
      }

      // Handle error cases with specific userType routing
      if (errorData?.userType) {
        showError(`Failed to submit registration: ${errorMessage}`, () => {
          // Clear form data and auth state
          clearUserStorage();

          // Clean up form data for this account
          if (currentAccountId) {
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith(`form_${currentAccountId}_`) || key.startsWith(`status_${currentAccountId}_`)) {
                localStorage.removeItem(key);
              }
            });
          }

          // Redirect based on user type
          const userType = err.response.data.userType;
          console.log('User type from error response:', userType);

          switch (userType) {
            case 'admin':
            case 'officestaff':
              navigate('/dashboard');
              break;
            case 'fieldstaff':
            case 'outsourcestaff':
            case 'outsourcedstaff':
              navigate('/availability');
              break;
            default:
              navigate('/login');
          }
        });
      } else {
        // Generic error handling
        showError(`Failed to submit registration: ${errorMessage}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const total = visibleItems.length;
  const completed = visibleItems.filter(i => i.status === "complete").length;
  const inProgress = visibleItems.filter(i => i.status === "in-progress").length;
  const incomplete = total - completed - inProgress;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // if (loading) {
  //   return (
  //     <div className="max-w-4xl mx-auto h-screen flex items-center justify-center">
  //       <div className="text-center">
  //         <div className="text-2xl font-bold text-red-600">talent</div>
  //         <div className="text-sm text-gray-500">employment inc.</div>
  //         <div className="mt-4">Loading registration status...</div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="max-w-4xl mx-auto h-screen overflow-y-auto p-0 space-y-6 shadow rounded-lg mt-4 mb-4">
      <div className="p-6 bg-white">
        <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
          <h2 className="text-3xl font-bold mt-4" style={{ textAlign: "center" }}>Register</h2>
        </header>
      </div>
      <div className="progress-card p-6 bg-white border rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-600">Overall progress</div>
          <div className="text-sm font-medium">{completed}/{total}</div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-2 rounded-full bg-red-600" style={{ width: `${percent}%` }} />
        </div>
        <div className="mt-2 text-xs text-gray-500">
          {inProgress} in progress · {incomplete} remaining
        </div>
      </div>
      <div className="p-6 bg-white">
        {hidesOntarioOnly && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            The 2025 TDON TAX FORM is for Ontario residents only, so it is not required for your province ({province}).
          </div>
        )}
        {!isProfileComplete && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Complete the <strong>Profile</strong> section first to unlock the remaining registration forms.</span>
          </div>
        )}
        <div className="space-y-3">
          {visibleItems.map((item) => {
            const workPref = items.find((i) => i.id === "work-preferences");
            const isProfileLocked = isLockedByProfile(item);
            const isCertDisabled =
              !isProfileLocked &&
              item.id === "certifications" &&
              (!workPref || workPref.status !== "complete");
            const isGate = item.id === GATE_ITEM_ID && !isProfileComplete;

            return (
              <button
                key={item.id}
                className={`w-full flex items-center justify-between p-4 min-h-[60px] rounded-lg border ${
                  isProfileLocked
                    ? "bg-gray-50 blur-sm select-none cursor-not-allowed"
                    : isCertDisabled
                      ? "bg-gray-50 opacity-60 cursor-not-allowed"
                      : isGate
                        ? "border-red-500 ring-2 ring-red-200 hover:bg-gray-50"
                        : "hover:bg-gray-50"
                }`}
                // Locked-by-profile items stay clickable (not `disabled`) so the click can open the modal
                onClick={() => {
                  if (!isCertDisabled) {
                    handleItemClick(item);
                  }
                }}
                disabled={isCertDisabled}
                aria-disabled={isProfileLocked || undefined}
                aria-label={isProfileLocked ? `${item.label} (locked until Profile is complete)` : undefined}
              >
                <div className="flex items-center gap-3">
                  <StatusIcon status={item.status} />
                  <span className="text-sm font-medium text-left">
                    {item.label}
                  </span>
                </div>
                <StatusBadge status={item.status} />
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-6 bg-white">
        {submitErr && <div className="text-red-600 mb-2">{submitErr}</div>}
        <div className="space-y-3">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-900 disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "SUBMIT"}
          </button>
          <button
            onClick={handleLogout}
            className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            LOGOUT
          </button>
        </div>
      </div>
      <ProfileRequiredModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onGoToProfile={() => {
          setShowProfileModal(false);
          navigate(`/register/${GATE_ITEM_ID}`, { state: { items } });
        }}
      />
    </div>
  );
};

export default RegistrationChecklist;


